import React, { useEffect } from 'react';
import { useRouteError, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Container } from '@mui/material';
import { IconRefresh, IconHome, IconAlertTriangle } from '@tabler/icons-react';

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  const isChunkLoadError = 
    error?.message?.includes('dynamically imported module') ||
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Failed to fetch') ||
    error?.name === 'TypeError';

  useEffect(() => {
    if (isChunkLoadError) {
      const lastReload = sessionStorage.getItem('router_chunk_reload');
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 4000) {
        sessionStorage.setItem('router_chunk_reload', String(now));
        window.location.reload();
      }
    }
  }, [isChunkLoadError]);

  if (isChunkLoadError) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="100vh" 
        bgcolor="#0A0F1D" 
        color="#FFFFFF"
        fontFamily="Poppins, sans-serif"
      >
        <Box 
          sx={{
            width: 44,
            height: 44,
            border: '3px solid rgba(20, 184, 166, 0.2)',
            borderTop: '3px solid #14B8A6',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            mb: 2,
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }}
        />
        <Typography variant="h6" sx={{ color: '#F8FAFC', fontWeight: 500 }}>
          Updating Application
        </Typography>
        <Typography variant="body2" sx={{ color: '#94A3B8', mt: 0.5 }}>
          Fetching latest version...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="80vh" 
        textAlign="center"
        fontFamily="Poppins, sans-serif"
      >
        <Box 
          p={2} 
          borderRadius="50%" 
          bgcolor="rgba(239, 68, 68, 0.1)" 
          color="#EF4444" 
          mb={2}
          border="1px solid rgba(239, 68, 68, 0.2)"
        >
          <IconAlertTriangle size={40} stroke={1.75} />
        </Box>
        <Typography variant="h3" sx={{ fontWeight: 500, color: '#1E293B', mb: 1, fontSize: '1.5rem' }}>
          Application Update Needed
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B', mb: 3, maxWidth: 400, fontSize: '0.875rem' }}>
          {error?.statusText || error?.message || "A new update is available. Please reload to continue."}
        </Typography>
        <Box display="flex" gap={1.5}>
          <Button
            variant="contained"
            startIcon={<IconRefresh size={18} />}
            onClick={() => window.location.reload()}
            sx={{
              background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
              color: '#FFFFFF',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '13px',
              height: '38px',
              '&:hover': { background: '#0F766E' }
            }}
          >
            Reload Page
          </Button>
          <Button
            variant="outlined"
            startIcon={<IconHome size={18} />}
            onClick={() => navigate('/dashboard')}
            sx={{
              borderColor: '#CBD5E1',
              color: '#475569',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '13px',
              height: '38px',
              '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' }
            }}
          >
            Dashboard
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
